import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useAppStore } from '../../app/appStore';
import { useCountryStore } from '../../shared/country/countryStore';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { resetForgotPassword } from './api';
import { CountryDetectedHint, CountryPicker } from './country/CountryPicker';
import { useAuthIdentifier } from './useAuthIdentifier';
import { useOtpChallenge } from './useOtpChallenge';
import {
  AuthLangBar,
  BrandMini,
  ErrLine,
  HintLine,
  authErrorMessage,
  identifierOtpEligible,
  scrollAuthInputIntoView,
  AuthLegalFoot,
} from './authShared';

/**
 * Forgot-password: identifier + OTP + new password, in one step.
 *
 * Pulled out of `AuthPage` verbatim (Task 9) — same fields, same mutations,
 * same gating. `onResetSuccess` is the one seam back to the shell: a
 * completed reset has to show a banner on the *login* screen, which this
 * component has already navigated away from and unmounted by the time that
 * banner would render. `initialIdentifier` / `onIdentifierChange` are the
 * same shell-level draft Login and Register use, so arriving here via
 * "forgot password?" (or leaving via the "no account, want to register?"
 * prompt below) does not drop what was already typed.
 */
export function ForgotForm({
  onResetSuccess,
  initialIdentifier,
  onIdentifierChange,
}: {
  onResetSuccess: () => void;
  initialIdentifier: string;
  onIdentifierChange: (value: string) => void;
}) {
  const setRoute = useAppStore((state) => state.setRoute);
  const showToast = useAppStore((state) => state.showToast);
  const t = useI18nStore((state) => state.t);
  const country = useCountryStore((state) => state.country);

  const {
    identifier,
    setIdentifier: setIdentifierField,
    mode: identifierModeValue,
    isPhone,
    hint: identifierPhoneHint,
  } = useAuthIdentifier(initialIdentifier, onIdentifierChange);
  const [identifierFocused, setIdentifierFocused] = useState(false);
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const otpChallenge = useOtpChallenge('reset_password');

  const resetMutation = useMutation({
    mutationFn: () => resetForgotPassword({ identifier, password, otp }),
    onMutate: () => {
      showToast(t('authResettingPwd'), 'info');
    },
    onSuccess: () => {
      setPassword('');
      setOtp('');
      setRoute('auth.login');
      onResetSuccess();
      showToast(t('authPwdReset'), 'success');
    },
  });

  const otpEligible = identifierOtpEligible(identifier, country);
  const identifierOtpError = !!identifier.trim() && !otpEligible;
  const passwordInvalid = !!password && !/^(?=.*[A-Za-z])(?=.*\d).{6,32}$/.test(password);

  const canSubmit = otpEligible && otp.length >= 4 && /^(?=.*[A-Za-z])(?=.*\d).{6,32}$/.test(password);
  const primaryDisabled = !canSubmit || resetMutation.isPending;

  function handleIdentifierInput(value: string) {
    setIdentifierField(value);
    otpChallenge.reset();
  }

  async function requestForgotOtp() {
    if (!otpEligible) return;
    await otpChallenge.send(identifier);
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (canSubmit && !resetMutation.isPending) resetMutation.mutate();
  }

  return (
    <>
      <div className="px-4 pt-4 pb-1 flex items-center justify-between relative z-[1]">
        <button type="button" className="text-white/80 text-sm inline-flex items-center gap-1 tap" onClick={() => setRoute('auth.login')}><ArrowLeft size={16} aria-hidden="true" /><span>{t('backToLogin')}</span></button>
        <BrandMini />
      </div>
      <div className="flex-1 flex flex-col justify-start px-6 pt-8 pb-4 relative z-[1]">
        <div className="text-center mb-5">
          <h1 className="text-ink-1 text-xl font-bold">{t('forgotWelcome')}</h1>
          <p className="text-[12.5px] mt-1.5" style={{ color: 'var(--ds-ink-3)' }}>{t('forgotSub')}</p>
        </div>
        <form className="auth-card" onSubmit={submit} onFocusCapture={scrollAuthInputIntoView}>
          <div className={`auth-phone-field${identifierOtpError ? ' is-error' : ''}`}>
            {isPhone ? <CountryPicker /> : null}
            <input
              className={`auth-input${isPhone ? ' auth-phone' : ''}`}
              autoComplete="email"
              inputMode={isPhone ? 'tel' : 'email'}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              maxLength={254}
              value={identifier}
              onChange={(event) => handleIdentifierInput(event.target.value)}
              onFocus={() => setIdentifierFocused(true)}
              onBlur={() => setIdentifierFocused(false)}
              placeholder={t('placeholderIdentifier')}
            />
          </div>
          {isPhone ? <CountryDetectedHint /> : null}
          {identifierFocused && !identifier.trim() ? <HintLine text={t('identifierTip')} /> : null}
          {identifierPhoneHint ? <HintLine tone="amber" text={identifierPhoneHint} /> : null}
          {identifierOtpError && !identifierPhoneHint ? <HintLine tone="amber" text={t('authOtpNoUsername')} /> : null}

          <div className="flex gap-2 mt-2">
            <input className="auth-input auth-phone flex-1" autoComplete="one-time-code" inputMode="numeric" maxLength={6} value={otp} onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder={t('placeholderOtpCode')} />
            <button type="button" className="bg-brand-600/20 text-brand-300 font-semibold px-4 rounded-xl text-sm disabled:opacity-50 whitespace-nowrap tap" disabled={!otpEligible || otpChallenge.isPending || otpChallenge.cooldownLeft > 0} onClick={() => { void requestForgotOtp(); }}>{otpChallenge.cooldownLeft > 0 ? t('resendInSec').replace('%n%', String(otpChallenge.cooldownLeft)) : t('sendOtp')}</button>
          </div>
          {otpChallenge.precheck === 'notfound' ? (
            <div className="ds-otp-nofound" data-otp-notfound-react="true">
              <span>{t('otpNoAccount')}</span>
              <button type="button" className="tap" onClick={() => { otpChallenge.reset(); setRoute('auth.register'); }}>{t('createAccountAction')} ›</button>
            </div>
          ) : null}

          <div className="auth-input-wrap mt-2">
            <input className="auth-input pr-11" autoComplete="new-password" type={showPassword ? 'text' : 'password'} value={password} onChange={(event) => setPassword(event.target.value)} placeholder={t('placeholderPwdNew')} />
            <button type="button" className="auth-input-eye tap" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? t('hidePwd') : t('showPassword')}>{showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}</button>
          </div>
          <p className={`text-[11px] mt-1 ml-1 ${passwordInvalid ? 'text-rose-400' : 'text-white/40'}`}>{t('passwordRuleHint')}</p>

          {otpChallenge.isError ? <ErrLine className="mt-1" text={authErrorMessage(otpChallenge.error, t, identifierModeValue, 'authOtpFail')} /> : null}
          {resetMutation.isError ? <ErrLine className="mt-1" text={authErrorMessage(resetMutation.error, t, identifierModeValue, 'authForgotFail')} /> : null}

          <button className="btn-gold-primary w-full py-3 rounded-xl mt-3 transition-all tap" type="submit" disabled={primaryDisabled}>
            {t('forgotAction')}
          </button>
        </form>
        <div className="flex justify-center mt-2"><AuthLangBar /></div>
        <div className="mt-2"><AuthLegalFoot /></div>
      </div>
    </>
  );
}
